import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatDateID } from "@/lib/format";

const EMPTY = { title: "", description: "", location: "", date: "", time: "", category: "Kopdar", image_url: "" };
const CATS = ["Kopdar", "Touring", "Baksos", "Workshop"];
const inputCls = "border-white/10 bg-[#0B0D15] text-slate-200 placeholder:text-slate-500";

export const EventsTab = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/events").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({ title: ev.title, description: ev.description, location: ev.location, date: ev.date, time: ev.time, category: ev.category, image_url: ev.image_url });
    setOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Ukuran file melebihi 50 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);

    try {
      const res = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: res.data.url }));
      toast.success("Gambar event berhasil diunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/events/${editing.id}`, form);
      else await api.post("/admin/events", form);
      toast.success(editing ? "Event diperbarui" : "Event ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Hapus event "${ev.title}"?`)) return;
    await api.delete(`/admin/events/${ev.id}`);
    toast.success("Event dihapus");
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-events-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase text-white">Kelola Event</h2>
        <Button data-testid="admin-add-event-button" onClick={openNew} className="bg-rose-600 text-white hover:bg-rose-500">
          <Plus className="mr-2 h-4 w-4" /> Tambah Event
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-white/10 bg-[#12141C]/90">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Event</TableHead>
              <TableHead className="text-slate-400">Kategori</TableHead>
              <TableHead className="text-slate-400">Tanggal</TableHead>
              <TableHead className="text-slate-400">Lokasi</TableHead>
              <TableHead className="text-right text-slate-400">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((ev) => (
              <TableRow key={ev.id} data-testid={`admin-event-row-${ev.id}`} className="border-white/10">
                <TableCell className="max-w-xs truncate font-medium text-slate-200">{ev.title}</TableCell>
                <TableCell>
                  <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-amber-400">{ev.category}</span>
                </TableCell>
                <TableCell className="text-slate-400">{formatDateID(ev.date)} {ev.time && `• ${ev.time}`}</TableCell>
                <TableCell className="max-w-[200px] truncate text-slate-400">{ev.location}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`admin-event-edit-${ev.id}`} size="icon" variant="ghost" onClick={() => openEdit(ev)}
                    className="text-slate-400 hover:text-amber-400"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`admin-event-delete-${ev.id}`} size="icon" variant="ghost" onClick={() => remove(ev)}
                    className="text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow className="border-white/10"><TableCell colSpan={5} className="py-8 text-center text-slate-500">Belum ada event.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#11131F]" data-testid="admin-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase text-white">{editing ? "Edit Event" : "Tambah Event"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nama Event *</Label>
              <Input data-testid="event-form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-slate-300">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="event-form-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#11131F] text-slate-200">
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Tanggal</Label>
                <Input data-testid="event-form-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Waktu</Label>
                <Input data-testid="event-form-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="19:00 WIB" className={inputCls} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Lokasi</Label>
              <Input data-testid="event-form-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Deskripsi</Label>
              <Textarea data-testid="event-form-description-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Gambar dari Perangkat</Label>
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-amber-500/40 bg-[#0B0D15] p-4 text-center transition-all hover:border-amber-400 hover:bg-[#0E111C]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex flex-col items-center py-2 text-rose-400">
                    <Loader2 className="mb-2 h-6 w-6 animate-spin" />
                    <span className="text-sm font-medium">Mengunggah gambar...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-1.5 flex items-center justify-center gap-2 text-amber-400 group-hover:text-amber-300">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm font-semibold">Upload Gambar dari Perangkat</span>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      JPG, PNG, WebP, GIF — maksimal 50 MB.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="relative my-0.5 text-center">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <span className="relative bg-[#11131F] px-3 font-mono text-[11px] text-slate-500">
                atau tempel link
              </span>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">URL Gambar</Label>
              <Input data-testid="event-form-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className={inputCls} />
            </div>

            {form.image_url && <img src={form.image_url} alt="Pratinjau Gambar Event" className="h-40 w-full rounded-lg border border-white/10 object-cover" />}

            <Button data-testid="event-form-save-button" onClick={save} disabled={saving || uploading || !form.title.trim()}
              className="bg-rose-600 font-bold text-white hover:bg-rose-500">
              {saving ? "Menyimpan..." : "Simpan Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

